import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
const FULL = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Category-based color so the calendar is visually coded.
const CAT_COLOR = {
  Vegetable: "bg-primary",
  Herb: "bg-secondary",
  Flower: "bg-accent",
  Fruit: "bg-chart-4",
  Custom: "bg-muted-foreground",
};

export default function HarvestCalendar({ plants: plantsProp = null, focusMonths = null, focusName = "" }) {
  const [plants, setPlants] = useState(plantsProp || []);
  const [loading, setLoading] = useState(!plantsProp);
  const currentMonth = new Date().getMonth() + 1;

  useEffect(() => {
    if (plantsProp) { setPlants(plantsProp); return; }
    if (focusMonths) return;
    api.get("/harvest-calendar").then((r) => setPlants(r.data.plants || [])).finally(() => setLoading(false));
  }, [plantsProp, focusMonths]);

  // Single-plant focus mode (used on Plant Detail)
  if (focusMonths) {
    return (
      <div className="sage-card p-6" data-testid="harvest-calendar-focus">
        <div className="label-overline mb-2">Best harvest months</div>
        <h3 className="font-serif text-2xl font-light mb-4">{focusName}</h3>
        <div className="grid grid-cols-12 gap-1.5">
          {MONTHS.map((m, i) => {
            const month = i + 1;
            const on = focusMonths.includes(month);
            const isNow = month === currentMonth;
            return (
              <div key={i} className="text-center">
                <div className="text-[10px] uppercase text-muted-foreground mb-1">{m}</div>
                <div
                  className={`h-9 rounded-md flex items-center justify-center text-xs font-medium transition-all ${
                    on ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground"
                  } ${isNow ? "ring-2 ring-accent" : ""}`}
                  title={FULL[i]}
                  data-testid={`month-${month}`}
                >
                  {isNow ? "•" : ""}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          The dot marks this month. Filled cells are your strongest harvest windows.
        </div>
      </div>
    );
  }

  if (loading) return <div className="text-sm text-muted-foreground">Reading the almanac…</div>;
  if (!plants.length) {
    return (
      <div className="sage-card p-8 text-center text-sm text-muted-foreground italic" data-testid="harvest-calendar-empty">
        Add plants to your garden to see your harvest calendar.
      </div>
    );
  }

  return (
    <div className="sage-card p-6 overflow-x-auto" data-testid="harvest-calendar">
      <div className="min-w-[640px]">
        <div className="grid grid-cols-[160px_repeat(12,1fr)] gap-1.5 items-center">
          <div className="label-overline">Plant</div>
          {MONTHS.map((m, i) => (
            <div key={i} className={`text-center text-[10px] uppercase tracking-wider ${i + 1 === currentMonth ? "text-accent font-semibold" : "text-muted-foreground"}`} title={FULL[i]}>
              {m}
            </div>
          ))}
          {plants.map((p) => (
            <React.Fragment key={p.id}>
              <div className="text-sm font-medium truncate pr-2" title={p.name}>{p.name}</div>
              {Array.from({ length: 12 }, (_, i) => {
                const month = i + 1;
                const on = (p.months || []).includes(month);
                const isNow = month === currentMonth;
                const color = CAT_COLOR[p.category] || "bg-primary";
                return (
                  <div
                    key={i}
                    className={`h-6 rounded transition-all ${on ? color : "bg-muted/30"} ${isNow ? "ring-1 ring-accent" : ""} ${on ? "opacity-100" : "opacity-100"}`}
                    title={`${p.name} — ${FULL[i]}${on ? " (harvest)" : ""}`}
                    data-testid={`hc-${p.id}-${month}`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <Legend />
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 mt-4 text-xs text-muted-foreground">
      {Object.entries(CAT_COLOR).map(([cat, color]) => (
        <span key={cat} className="inline-flex items-center gap-1.5">
          <span className={`inline-block w-3 h-3 rounded ${color}`} /> {cat}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded ring-1 ring-accent bg-muted/30" /> Current month
      </span>
    </div>
  );
}
